/*
  Pagination (§6.2, D23). Replaces MUI's TablePagination and the old wrapper around it,
  which task 11 deleted once this had taken its last consumer.

  Same contract as the old one — count, rowsPerPage, page, onChangePage,
  onChangeRowsPerPage — so tasks 7 to 10 swap the component and keep their handlers.

  Two deliberate differences:

  1. It writes NOTHING to the URL (D23). The old wrapper pushed ?page= and ?perPage= on
     every page change through a routing helper, and the only code that read them back had
     zero callers — so the history entries bought nothing and a visitor's Back button
     walked through their own paging instead of leaving the page.

  2. The rows-per-page control is OPTIONAL, and it is optional by omission: no
     onChangeRowsPerPage, no control. SearchCardsRowTable is server-paginated with a fixed
     page size of 10 (SearchCardsRow.tsx:42, SearchCardsRowTable.tsx:91-99) and has no
     size to offer; without the variant task 10 would fork this component.

  `page` should be the CLAMPED page — selectTableRows returns it — so the control and the
  rows can never disagree about which page is showing.
*/

import { ChevronLeft, ChevronRight } from "lucide-react";

import Select from "components/Select";
import { CONTROL_BASE, FOCUS_RING } from "components/styles";

import {
  hasNextPage,
  hasPreviousPage,
  pageRangeLabel,
  ROWS_PER_PAGE_OPTIONS,
} from "./pagination";

export interface DataTablePaginationProps {
  page: number;
  rowsPerPage: number;
  /* Total rows being paged through — after filtering, not before. */
  count: number;
  onChangePage: (page: number) => void;
  /* Omit for a fixed page size and no rows-per-page control (D19's table, task 10). */
  onChangeRowsPerPage?: (rowsPerPage: number) => void;
  rowsPerPageOptions?: readonly number[];
  /*
    Names the navigation region. A page with two paginated tables needs two names, so it
    says which table this pages.
  */
  label?: string;
  className?: string;
}

const BUTTON_CLASS =
  `inline-flex h-9 w-9 items-center justify-center rounded-md ${CONTROL_BASE} hover:bg-pcg-raised ${FOCUS_RING}`;

const DataTablePagination = (props: DataTablePaginationProps) => {
  const {
    page,
    rowsPerPage,
    count,
    onChangePage,
    onChangeRowsPerPage,
    rowsPerPageOptions = ROWS_PER_PAGE_OPTIONS,
    label = "Table pagination",
    className = "",
  } = props;

  const canGoBack = hasPreviousPage(page, count, rowsPerPage);
  const canGoForward = hasNextPage(page, count, rowsPerPage);

  return (
    <nav
      aria-label={label}
      className={`flex flex-wrap items-end justify-between gap-3 py-3 ${className}`}
    >
      {onChangeRowsPerPage ? (
        <Select
          label="Rows per page"
          value={String(rowsPerPage)}
          options={rowsPerPageOptions.map((option) => ({
            value: String(option),
            label: String(option),
          }))}
          onChange={(value) => onChangeRowsPerPage(Number(value))}
          className="w-28"
        />
      ) : (
        /* Keeps the range and the buttons on the right when there is no size control. */
        <span aria-hidden="true" />
      )}

      <div className="flex items-center gap-2">
        {/*
          polite rather than assertive: paging is the visitor's own action, so the new
          range is worth hearing after the button they pressed, not over the top of it.
        */}
        <p aria-live="polite" className="text-sm text-pcg-textMuted">
          {pageRangeLabel(page, count, rowsPerPage)}
        </p>
        <button
          type="button"
          aria-label="Previous page"
          disabled={!canGoBack}
          onClick={() => onChangePage(page - 1)}
          className={BUTTON_CLASS}
        >
          <ChevronLeft aria-hidden="true" className="h-4 w-4" />
        </button>
        <button
          type="button"
          aria-label="Next page"
          disabled={!canGoForward}
          onClick={() => onChangePage(page + 1)}
          className={BUTTON_CLASS}
        >
          <ChevronRight aria-hidden="true" className="h-4 w-4" />
        </button>
      </div>
    </nav>
  );
};

export default DataTablePagination;
